import axios from 'axios';
import {API_BASE_URL} from './authApi';

export interface SafetyPreferences {
  blockedWords: string[];
  blockObscenity: boolean;
  locationSharingEnabled: boolean;
  updatedAt: string;
}

export interface SafetyPreferencesUpdate {
  blockedWords: string[];
  blockObscenity: boolean;
  locationSharingEnabled: boolean;
}

export interface SafetyBlockedUser {
  _id: string;
  profile?: {
    name?: string;
    profilePicture?: string;
  };
}

export type SafetyReportReason = 'spam' | 'harassment' | 'inappropriate' | 'other';

interface SafetyPreferencesResponse {
  success: boolean;
  preferences: SafetyPreferences;
}

interface SafetyBlockedUsersResponse {
  success: boolean;
  blockedUsers: SafetyBlockedUser[];
}

interface SafetyMutationResponse {
  success: boolean;
}

type SafetyApiErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

export class SafetyApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'SafetyApiError';
    this.status = status;
  }
}

const safetyClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as SafetyApiErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

const requestWithAuth = async <T>(
  token: string,
  request: () => Promise<{data: T}>,
  fallbackMessage: string
): Promise<T> => {
  try {
    const response = await request();
    return response.data;
  } catch (error) {
    throw new SafetyApiError(
      getErrorMessage(error, fallbackMessage),
      axios.isAxiosError(error) ? error.response?.status : undefined
    );
  }
};

export const safetyApi = {
  async getPreferences(token: string): Promise<SafetyPreferences> {
    const response = await requestWithAuth<SafetyPreferencesResponse>(
      token,
      () => safetyClient.get('/safety/preferences', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      'Failed to load safety preferences'
    );

    return response.preferences;
  },

  async updatePreferences(token: string, payload: SafetyPreferencesUpdate): Promise<SafetyPreferences> {
    const response = await requestWithAuth<SafetyPreferencesResponse>(
      token,
      () => safetyClient.put('/safety/preferences', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      'Failed to update safety preferences'
    );

    return response.preferences;
  },

  async getBlockedUsers(token: string): Promise<SafetyBlockedUser[]> {
    const response = await requestWithAuth<SafetyBlockedUsersResponse>(
      token,
      () => safetyClient.get('/blocks', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      'Failed to load blocked users'
    );

    return Array.isArray(response.blockedUsers) ? response.blockedUsers : [];
  },

  async blockUser(token: string, userId: string): Promise<void> {
    await requestWithAuth<SafetyMutationResponse>(
      token,
      () => safetyClient.post('/blocks', {userId}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      'Failed to block this user'
    );
  },

  async unblockUser(token: string, userId: string): Promise<void> {
    await requestWithAuth<SafetyMutationResponse>(
      token,
      () => safetyClient.delete(`/blocks/${encodeURIComponent(userId)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      'Failed to unblock this user'
    );
  },

  async reportUser(
    token: string,
    payload: {
      reportedUserId: string;
      reason: SafetyReportReason;
      additionalDetails?: string;
    }
  ): Promise<void> {
    await requestWithAuth<SafetyMutationResponse>(
      token,
      () => safetyClient.post('/reports', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      'Failed to submit report'
    );
  },
};
